import { StatCard } from "./StatCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BookOpen, ClipboardCheck, Users, TrendingUp, Activity, GraduationCap,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  ChartContainer, ChartTooltip, ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { useAuth, AppRole } from "@/contexts/AuthContext";

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent-orange))",
  "hsl(var(--accent-blue))",
  "hsl(var(--destructive))",
];

const roleTitles: Record<string, string> = {
  hod_cmt: "Computer Technology",
  hod_pst: "Pastoral Studies",
  hod_sw: "Social Work",
  it_incharge: "IT Operations",
};

export function HodDashboard() {
  const { roles } = useAuth();

  const activeRole = (["hod_cmt", "hod_pst", "hod_sw", "it_incharge"] as AppRole[]).find(
    (r) => roles.includes(r)
  );
  const departmentLabel = activeRole ? roleTitles[activeRole] : "Department";

  // Course stats
  const { data: courseStats } = useQuery({
    queryKey: ["hod-courses"],
    queryFn: async () => {
      const { data } = await supabase.from("courses").select("id, program, semester");
      return {
        total: data?.length ?? 0,
        programs: [...new Set(data?.map((c) => c.program).filter(Boolean))].length,
        semesters: [...new Set(data?.map((c) => c.semester).filter(Boolean))].length,
      };
    },
  });

  // Registration stats
  const { data: regStats } = useQuery({
    queryKey: ["hod-registrations"],
    queryFn: async () => {
      const { data } = await supabase.from("registrations").select("status");
      return {
        total: data?.length ?? 0,
        registered: data?.filter((r) => r.status === "registered").length ?? 0,
        dropped: data?.filter((r) => r.status === "dropped").length ?? 0,
      };
    },
  });

  // Student count
  const { data: studentCount = 0 } = useQuery({
    queryKey: ["hod-students"],
    queryFn: async () => {
      const { count } = await supabase.from("students").select("*", { count: "exact", head: true }).eq("status", "active");
      return count ?? 0;
    },
  });

  // Exam results
  const { data: examStats } = useQuery({
    queryKey: ["hod-exams"],
    queryFn: async () => {
      const { data } = await supabase.from("exam_results").select("status, marks, grade");
      const total = data?.length ?? 0;
      const pending = data?.filter((r) => r.status === "pending").length ?? 0;
      const published = data?.filter((r) => r.status === "published").length ?? 0;
      const avgMarks = total
        ? Math.round(data!.reduce((s, r) => s + (Number(r.marks) || 0), 0) / total)
        : 0;

      // Grade distribution
      const grades: Record<string, number> = {};
      data?.forEach((r) => {
        if (r.grade) grades[r.grade] = (grades[r.grade] || 0) + 1;
      });

      return { total, pending, published, avgMarks, grades };
    },
  });

  const gradeChartData = Object.entries(examStats?.grades ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([grade, count]) => ({ name: grade, value: count }));

  const overviewData = [
    { name: "Students", value: studentCount },
    { name: "Courses", value: courseStats?.total ?? 0 },
    { name: "Registrations", value: regStats?.total ?? 0 },
    { name: "Results", value: examStats?.total ?? 0 },
  ];

  return (
    <>
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            {departmentLabel} Overview
          </CardTitle>
          <CardDescription>
            Academic and examination statistics (view-only access)
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Students"
          value={studentCount.toLocaleString()}
          icon={Users}
          description="Currently enrolled"
        />
        <StatCard
          title="Total Courses"
          value={courseStats?.total ?? 0}
          icon={BookOpen}
          description={`${courseStats?.programs ?? 0} programs`}
        />
        <StatCard
          title="Course Registrations"
          value={regStats?.total ?? 0}
          icon={ClipboardCheck}
          description={`${regStats?.registered ?? 0} active`}
          trend="up"
        />
        <StatCard
          title="Average Marks"
          value={`${examStats?.avgMarks ?? 0}%`}
          icon={TrendingUp}
          description={`${examStats?.total ?? 0} results recorded`}
          trend={examStats?.avgMarks && examStats.avgMarks >= 50 ? "up" : "down"}
        />
      </div>

      <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Pending Results"
          value={examStats?.pending ?? 0}
          icon={ClipboardCheck}
          description="Awaiting approval"
        />
        <StatCard
          title="Published Results"
          value={examStats?.published ?? 0}
          icon={Activity}
          description="Visible to students"
          trend="up"
        />
        <StatCard
          title="Dropped Registrations"
          value={regStats?.dropped ?? 0}
          icon={Activity}
          description="Course drops"
        />
        <StatCard
          title="Semesters Offered"
          value={courseStats?.semesters ?? 0}
          icon={BookOpen}
          description="Across all programs"
        />
      </div>

      <div className="grid gap-2 sm:gap-3 grid-cols-1 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Academic Summary</CardTitle>
            <CardDescription>Students, courses, registrations & results</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                value: { label: "Count", color: "hsl(var(--primary))" },
              }}
              className="h-[250px]"
            >
              <BarChart data={overviewData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Grade Distribution</CardTitle>
            <CardDescription>Breakdown of published grades</CardDescription>
          </CardHeader>
          <CardContent>
            {gradeChartData.length === 0 ? (
              <div className="flex items-center justify-center h-[250px] text-sm text-muted-foreground">
                No grade data available yet
              </div>
            ) : (
              <ChartContainer
                config={{
                  value: { label: "Students", color: "hsl(var(--primary))" },
                }}
                className="h-[250px]"
              >
                <PieChart>
                  <Pie
                    data={gradeChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    nameKey="name"
                    label
                  >
                    {gradeChartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
